
import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, Eye, Edit, History, AlertTriangle, X, Plus,
  ChevronRight, MapPin, Package, ArrowRightLeft, ArrowUpRight, ArrowDownRight, CheckCircle, XCircle, Clock, Info, Loader2,
  ArrowUpDown, ArrowUp, ArrowDown, Truck, PenTool, ClipboardCheck, FileText, FileSpreadsheet
} from 'lucide-react';
import { Button, Input, Badge, Card, Modal, ConfirmationDialog, FieldHint, InfoTooltip, useToast } from './ui';
import { ProductType, MovementType } from '../types';
import {
  stockService,
  apiClient,
  ProductListItem,
  StockMovement as ApiStockMovement,
  StockMovementCreate,
  StockByProduct,
  Site,
  Category,
  ProductCreate,
  ProductUpdate,
  ProductDetail as ApiProductDetail,
  MovementStatusType,
  ExecuteSortieData,
  ExecuteEntreeData,
} from '../src/services';
import { useMutation } from '../src/hooks/useApi';
import useAuth from '../src/hooks/useAuth';

// Type adapter: map API product to component-friendly format
interface Product {
  id: string;
  reference: string;
  name: string;
  category: string;
  type: ProductType;
  isSerialized: boolean;
  minStockThreshold: number;
  stockTotal: number;
  stockDisponible: number;
  stockReservee: number;
  stockEnLocation: number;
  stockEnLoa: number;
  stockBySite: Record<string, number>;
  priceSale: number;
  priceRental: number;
  purchasePrice: number;
  imageUrl?: string;
  status: 'OK' | 'CRITICAL';
  description?: string;
}

// Map API types to frontend enums
const mapProductType = (apiType: 'MACHINE' | 'PART' | 'CONSUMABLE'): ProductType => {
  const mapping: Record<string, ProductType> = {
    'MACHINE': ProductType.MACHINE,
    'PART': ProductType.PART,
    'CONSUMABLE': ProductType.CONSUMABLE,
  };
  return mapping[apiType] || ProductType.PART;
};

const mapMovementType = (apiType: string): MovementType => {
  const mapping: Record<string, MovementType> = {
    'ENTREE': MovementType.IN,
    'SORTIE': MovementType.OUT,
    'VENTE': MovementType.OUT,
    'LOCATION': MovementType.OUT,
    'LOA': MovementType.OUT,
    'RETOUR': MovementType.IN,
    'TRANSFERT': MovementType.TRANSFER,
    'AJUSTEMENT': MovementType.ADJUSTMENT,
    'CORRECTION': MovementType.ADJUSTMENT,
  };
  return mapping[apiType] || MovementType.OUT;
};

const mapToApiMovementType = (type: MovementType): 'ENTREE' | 'SORTIE' | 'TRANSFERT' | 'AJUSTEMENT' => {
  const mapping: Record<MovementType, 'ENTREE' | 'SORTIE' | 'TRANSFERT' | 'AJUSTEMENT'> = {
    [MovementType.IN]: 'ENTREE',
    [MovementType.OUT]: 'SORTIE',
    [MovementType.TRANSFER]: 'TRANSFERT',
    [MovementType.ADJUSTMENT]: 'AJUSTEMENT',
  };
  return mapping[type] || 'SORTIE';
};

// --- Formatters ---
const formatCurrency = (val: number) => new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(val);

// --- Sub-components ---

const StockStatusBadge = ({ product }: { product: Product }) => {
  const isCritical = product.stockTotal <= product.minStockThreshold;
  return (
    <Badge color={isCritical ? 'red' : 'green'} className="flex items-center gap-1">
       {isCritical && <AlertTriangle className="w-3 h-3" />}
       {isCritical ? 'CRITIQUE' : 'OK'}
    </Badge>
  );
};

const MovementTypeBadge = ({ type }: { type: MovementType }) => {
  const styles = {
    [MovementType.IN]: { color: 'green', icon: ArrowDownRight },
    [MovementType.OUT]: { color: 'red', icon: ArrowUpRight },
    [MovementType.TRANSFER]: { color: 'blue', icon: ArrowRightLeft },
    [MovementType.ADJUSTMENT]: { color: 'yellow', icon: AlertTriangle },
  };
  const config = styles[type];
  const Icon = config.icon;
  return (
    <Badge color={config.color as any} className="flex items-center gap-1">
      <Icon className="w-3 h-3" /> {type}
    </Badge>
  );
};

const MovementStatusBadge = ({ statut }: { statut?: MovementStatusType }) => {
  if (statut === 'EXECUTE') {
    return <Badge color="green" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Exécuté</Badge>;
  }
  if (statut === 'VALIDE') {
    return <Badge color="blue" className="flex items-center gap-1"><ClipboardCheck className="w-3 h-3" /> Validé</Badge>;
  }
  if (statut === 'EN_TRANSIT') {
    return <Badge color="yellow" className="flex items-center gap-1 !bg-orange-100 !text-orange-800"><Truck className="w-3 h-3" /> En transit</Badge>;
  }
  if (statut === 'EN_ATTENTE') {
    return <Badge color="yellow" className="flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</Badge>;
  }
  if (statut === 'REJETE') {
    return <Badge color="red" className="flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejeté</Badge>;
  }
  // Legacy fallback
  return <Badge color="green" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Exécuté</Badge>;
};

// --- Create Movement Modal ---

interface CreateMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  sites: Site[];
  onSuccess: () => void;
}

const CreateMovementModal = ({ isOpen, onClose, product, sites, onSuccess }: CreateMovementModalProps) => {
  const [formData, setFormData] = useState({
    type: MovementType.OUT,
    quantity: 1,
    siteSource: '',
    siteDestination: '',
    refDoc: ''
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !formData.siteSource) {
      const defaultSite = sites.find(s => s.is_default) || sites[0];
      setFormData(prev => ({ ...prev, siteSource: defaultSite.id }));
    }
  }, [sites, formData.siteSource]);

  const { toast } = useToast();
  const { mutate, isLoading, error, reset } = useMutation(stockService.createMovement, {
    onSuccess: (result: any) => {
      setIsConfirmOpen(false);
      const statut = result?.statut;
      if (statut === 'EXECUTE') {
        toast('Mouvement créé et exécuté automatiquement', 'success');
      } else {
        toast('Mouvement enregistré, en attente de validation par le Gérant', 'info');
      }
      onSuccess();
      onClose();
      setFormData({
        type: MovementType.OUT,
        quantity: 1,
        siteSource: sites[0]?.id || '',
        siteDestination: '',
        refDoc: ''
      });
    },
  });

  if (!product || !isOpen) return null;

  const handlePreSubmit = () => {
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (formData.type === MovementType.TRANSFER && formData.siteSource === formData.siteDestination) {
      return; // Should not happen due to filter, but guard anyway
    }
    const movementData: StockMovementCreate = {
      product_id: product.id,
      type_mouvement: mapToApiMovementType(formData.type),
      quantite: formData.quantity,
      site_source_id: formData.siteSource,
      site_destination_id: formData.type === MovementType.TRANSFER ? formData.siteDestination : undefined,
      reference_document: formData.refDoc?.trim(),
    };

    await mutate(movementData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title={`Nouveau Mouvement : ${product.reference}`}>
        <div className="space-y-6">
            <div className="space-y-4 animate-in fade-in">
              {/* Error Banner */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Warning Banner */}
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <div className="ml-3 text-sm text-amber-700">
                    <p className="font-bold">Rappel Audit</p>
                    <p>Tout mouvement validé est irréversible et consigné dans le journal d'audit.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type de Mouvement <span className="text-red-500 ml-0.5">*</span></label>
                  <select
                    className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                    value={formData.type}
                    onChange={e => setFormData({...formData, type: e.target.value as MovementType})}
                    disabled={isLoading}
                  >
                    <option value={MovementType.OUT}>Sortie (Vente/Loc/Perte)</option>
                    <option value={MovementType.IN}>Entrée (Achat/Retour)</option>
                    <option value={MovementType.TRANSFER}>Transfert Inter-sites</option>
                    <option value={MovementType.ADJUSTMENT}>Ajustement Inventaire</option>
                  </select>
                  <FieldHint>Détermine le sens et l'impact sur le stock</FieldHint>
                </div>
                <Input
                  label="Quantité"
                  type="number"
                  min={1}
                  required
                  max={product.isSerialized ? 1 : undefined}
                  value={formData.quantity}
                  onChange={e => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                  disabled={product.isSerialized || isLoading}
                  tooltip={product.isSerialized ? "Fixé à 1 pour les produits avec numéro de série" : undefined}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Site Source <span className="text-red-500 ml-0.5">*</span></label>
                  <select
                    className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                    value={formData.siteSource}
                    onChange={e => setFormData({...formData, siteSource: e.target.value})}
                    disabled={isLoading}
                  >
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.nom} {site.is_default ? '(Siège)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.type === MovementType.TRANSFER && (
                   <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Site Destination <span className="text-red-500 ml-0.5">*</span></label>
                   <select
                     className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                     value={formData.siteDestination}
                     onChange={e => setFormData({...formData, siteDestination: e.target.value})}
                     disabled={isLoading}
                   >
                     <option value="">Sélectionner...</option>
                     {sites.filter(s => s.id !== formData.siteSource).map(site => (
                       <option key={site.id} value={site.id}>
                         {site.nom}
                       </option>
                     ))}
                   </select>
                 </div>
                )}
              </div>

              <Input
                label="Document de Référence (BL, Facture...)"
                placeholder="ex: BL-2024-001"
                required
                hint="N° de bon de livraison, facture ou bon interne — requis pour l'audit"
                value={formData.refDoc}
                onChange={e => setFormData({...formData, refDoc: e.target.value})}
                disabled={isLoading}
              />

              <div className="pt-4 flex justify-end gap-3">
                <Button variant="ghost" onClick={handleClose} disabled={isLoading}>Annuler</Button>
                <Button
                  onClick={handlePreSubmit}
                  disabled={!formData.refDoc?.trim() || isLoading || (formData.type === MovementType.TRANSFER && !formData.siteDestination)}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  Créer Mouvement
                </Button>
              </div>
            </div>
        </div>
      </Modal>

      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
        title="Confirmer la création du mouvement ?"
        message={`Vous êtes sur le point de créer un mouvement de type ${formData.type} de ${formData.quantity} unités de "${product.name}". Le mouvement sera soumis à validation par le Gérant avant d'être appliqué au stock.`}
        confirmLabel={isLoading ? "Traitement..." : "Confirmer la création"}
      />
    </>
  );
};

// --- Create Product Modal ---

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ProductTypeOption {
  id: string;
  libelle: string;
  base_type: 'MACHINE' | 'PART' | 'CONSUMABLE';
}

const CreateProductModal = ({ isOpen, onClose, onSuccess }: CreateProductModalProps) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [productTypes, setProductTypes] = useState<ProductTypeOption[]>([]);
  const [productTypesLoading, setProductTypesLoading] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    reference: '',
    designation: '',
    description: '',
    type: 'PART' as 'MACHINE' | 'PART' | 'CONSUMABLE',
    type_produit_id: '',
    categorie_id: '',
    numero_serie: '',
    marque: '',
    modele: '',
    annee_fabrication: '',
    prix_achat: '',
    prix_vente_public: '',
    prix_vente_grossiste: '',
    prix_vente_revendeur: '',
    tarif_location_journalier: '',
    seuil_alerte: '5',
    stock_initial: '',
    site_stock_id: '',
    motif_stock_initial: '',
  });

  useEffect(() => {
    if (isOpen) {
      setCategoriesLoading(true);
      stockService.listCategories()
        .then(cats => setCategories(cats))
        .catch(() => setCategories([]))
        .finally(() => setCategoriesLoading(false));

      setProductTypesLoading(true);
      apiClient.get('/product-types', { params: { active_only: true } })
        .then(res => {
          const data = res.data?.data || [];
          setProductTypes(data);
        })
        .catch(() => setProductTypes([]))
        .finally(() => setProductTypesLoading(false));

      stockService.listSites()
        .then(s => setSites(s))
        .catch(() => setSites([]));
    }
  }, [isOpen]);

  // Derive base_type from selected product type
  const selectedType = productTypes.find(pt => pt.id === formData.type_produit_id);
  const baseType = selectedType?.base_type || formData.type;

  const { toast } = useToast();
  const { mutate, isLoading, error, reset } = useMutation(stockService.createProduct, {
    onSuccess: () => {
      toast('Produit créé avec succès', 'success');
      onSuccess();
      handleClose();
    },
  });

  const handleClose = () => {
    reset();
    setValidationError(null);
    setFormData({
      reference: '',
      designation: '',
      description: '',
      type: 'PART',
      type_produit_id: '',
      categorie_id: '',
      numero_serie: '',
      marque: '',
      modele: '',
      annee_fabrication: '',
      prix_achat: '',
      prix_vente_public: '',
      prix_vente_grossiste: '',
      prix_vente_revendeur: '',
      tarif_location_journalier: '',
      seuil_alerte: '5',
      stock_initial: '',
      site_stock_id: '',
      motif_stock_initial: '',
    });
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Frontend validation for required fields
    setValidationError(null);
    if (!formData.reference?.trim()) {
      setValidationError('La référence est obligatoire');
      return;
    }
    if (!formData.designation?.trim()) {
      setValidationError('La désignation est obligatoire');
      return;
    }
    if (!formData.categorie_id) {
      setValidationError('La catégorie est obligatoire');
      return;
    }
    // Derive type from selected product type or form fallback
    const effectiveType = selectedType?.base_type || formData.type;
    const data: ProductCreate = {
      reference: formData.reference,
      designation: formData.designation,
      type: effectiveType,
      categorie_id: formData.categorie_id,
      type_produit_id: formData.type_produit_id || undefined,
      description: formData.description || undefined,
      numero_serie: formData.numero_serie || undefined,
      marque: formData.marque || undefined,
      modele: formData.modele || undefined,
      annee_fabrication: formData.annee_fabrication ? parseInt(formData.annee_fabrication) : undefined,
      seuil_alerte: parseInt(formData.seuil_alerte) || 5,
      prix_achat: formData.prix_achat ? parseFloat(formData.prix_achat) : undefined,
      prix_vente_public: formData.prix_vente_public ? parseFloat(formData.prix_vente_public) : undefined,
      prix_vente_grossiste: formData.prix_vente_grossiste ? parseFloat(formData.prix_vente_grossiste) : undefined,
      prix_vente_revendeur: formData.prix_vente_revendeur ? parseFloat(formData.prix_vente_revendeur) : undefined,
      tarif_location_journalier: formData.tarif_location_journalier ? parseFloat(formData.tarif_location_journalier) : undefined,
      stock_initial: formData.stock_initial ? parseInt(formData.stock_initial) : undefined,
      site_stock_id: formData.site_stock_id || undefined,
      motif_stock_initial: formData.motif_stock_initial || undefined,
    };
    await mutate(data);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Nouveau Produit">
      <form onSubmit={handleSubmit} className="space-y-4">
        {(validationError || error) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {validationError || error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Référence"
            placeholder="ex: MAC-001"
            required
            hint="Code unique, ex : MAC-001"
            value={formData.reference}
            onChange={e => setFormData({ ...formData, reference: e.target.value })}
            disabled={isLoading}
          />
          <Input
            label="Désignation"
            placeholder="ex: Tracteur John Deere 5050"
            required
            hint="Nom commercial affiché dans les devis et factures"
            value={formData.designation}
            onChange={e => setFormData({ ...formData, designation: e.target.value })}
            disabled={isLoading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type <span className="text-red-500">*</span></label>
            {productTypes.length > 0 ? (
              <select
                className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                value={formData.type_produit_id}
                onChange={e => {
                  const pt = productTypes.find(t => t.id === e.target.value);
                  setFormData({
                    ...formData,
                    type_produit_id: e.target.value,
                    type: pt?.base_type || 'PART',
                  });
                }}
                required
                disabled={isLoading || productTypesLoading}
              >
                <option value="">Sélectionner...</option>
                {productTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.libelle}</option>
                ))}
              </select>
            ) : (
              <select
                className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value as 'MACHINE' | 'PART' | 'CONSUMABLE' })}
                required
                disabled={isLoading}
              >
                <option value="PART">Pièce</option>
                <option value="MACHINE">Machine</option>
                <option value="CONSUMABLE">Consommable</option>
              </select>
            )}
            <FieldHint>Détermine le comportement (numéro de série, location…)</FieldHint>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie <span className="text-red-500">*</span></label>
            <select
              className="w-full border-slate-300 rounded-md shadow-sm text-sm"
              value={formData.categorie_id}
              onChange={e => setFormData({ ...formData, categorie_id: e.target.value })}
              required
              disabled={isLoading || categoriesLoading}
            >
              <option value="">Sélectionner...</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.libelle}</option>
              ))}
            </select>
            <FieldHint>Utilisée pour les rapports et le filtrage du stock</FieldHint>
          </div>
        </div>

        {baseType === 'MACHINE' && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-4">
            <p className="text-xs font-bold text-blue-700 uppercase">Informations Machine</p>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="N° Série"
                placeholder="ex: SN-12345"
                tooltip="Identifiant unique gravé sur la machine, permet le suivi individuel"
                value={formData.numero_serie}
                onChange={e => setFormData({ ...formData, numero_serie: e.target.value })}
                disabled={isLoading}
              />
              <Input
                label="Marque"
                placeholder="ex: John Deere"
                value={formData.marque}
                onChange={e => setFormData({ ...formData, marque: e.target.value })}
                disabled={isLoading}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Modèle"
                placeholder="ex: 5050"
                value={formData.modele}
                onChange={e => setFormData({ ...formData, modele: e.target.value })}
                disabled={isLoading}
              />
              <Input
                label="Année de Fabrication"
                type="number"
                placeholder="ex: 2024"
                value={formData.annee_fabrication}
                onChange={e => setFormData({ ...formData, annee_fabrication: e.target.value })}
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-4">
          <p className="text-xs font-bold text-slate-600 uppercase">Tarification (FCFA)</p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Prix d'Achat"
              type="number"
              min={0}
              placeholder="0"
              tooltip="Coût d'acquisition HT, utilisé pour la valorisation FIFO du stock"
              value={formData.prix_achat}
              onChange={e => setFormData({ ...formData, prix_achat: e.target.value })}
              disabled={isLoading}
            />
            <Input
              label="Prix Vente Public"
              type="number"
              min={0}
              placeholder="0"
              tooltip="Prix catalogue pour les ventes aux clients finaux"
              value={formData.prix_vente_public}
              onChange={e => setFormData({ ...formData, prix_vente_public: e.target.value })}
              disabled={isLoading}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Prix Grossiste"
              type="number"
              min={0}
              placeholder="0"
              tooltip="Tarif préférentiel pour les achats en gros"
              value={formData.prix_vente_grossiste}
              onChange={e => setFormData({ ...formData, prix_vente_grossiste: e.target.value })}
              disabled={isLoading}
            />
            <Input
              label="Prix Revendeur"
              type="number"
              min={0}
              placeholder="0"
              tooltip="Tarif pour les partenaires revendeurs"
              value={formData.prix_vente_revendeur}
              onChange={e => setFormData({ ...formData, prix_vente_revendeur: e.target.value })}
              disabled={isLoading}
            />
            <Input
              label="Tarif Location/Jour"
              type="number"
              min={0}
              placeholder="0"
              tooltip="Tarif journalier par défaut pour les contrats de location"
              value={formData.tarif_location_journalier}
              onChange={e => setFormData({ ...formData, tarif_location_journalier: e.target.value })}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Seuil d'Alerte Stock"
            type="number"
            min={0}
            value={formData.seuil_alerte}
            hint="Alerte automatique dès que le stock passe sous ce seuil"
            onChange={e => setFormData({ ...formData, seuil_alerte: e.target.value })}
            disabled={isLoading}
          />
          <div className="col-span-1" />
        </div>

        {/* Stock Initial (optionnel) */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-4">
          <p className="text-xs font-bold text-emerald-700 uppercase">Stock Initial (optionnel)</p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Quantité initiale"
              type="number"
              min={1}
              placeholder="0"
              tooltip="Quantité de départ en stock, auto-validée à la création"
              value={formData.stock_initial}
              onChange={e => setFormData({ ...formData, stock_initial: e.target.value })}
              disabled={isLoading}
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site de stockage</label>
              <select
                className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                value={formData.site_stock_id}
                onChange={e => setFormData({ ...formData, site_stock_id: e.target.value })}
                disabled={isLoading}
                required={!!formData.stock_initial}
              >
                <option value="">Sélectionner...</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.nom} {site.is_default ? '(Siège)' : ''}
                  </option>
                ))}
              </select>
              <FieldHint>Requis si quantité initiale renseignée</FieldHint>
            </div>
          </div>
          {formData.stock_initial && (
            <Input
              label="Justification"
              placeholder="ex: Stock de démarrage, inventaire initial..."
              value={formData.motif_stock_initial}
              onChange={e => setFormData({ ...formData, motif_stock_initial: e.target.value })}
              disabled={isLoading}
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            className="w-full border-slate-300 rounded-md p-2 text-sm"
            rows={3}
            placeholder="Description du produit..."
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            disabled={isLoading}
          />
        </div>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-4">
          <Button variant="ghost" type="button" onClick={handleClose} disabled={isLoading}>Annuler</Button>
          <Button type="submit" disabled={isLoading || !formData.reference || !formData.designation || !formData.categorie_id || (!!formData.stock_initial && !formData.site_stock_id)}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Création...
              </>
            ) : (
              'Créer le Produit'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// --- Edit Product Modal ---

interface EditProductModalProps {
  isOpen: boolean;
  productId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditProductModal = ({ isOpen, productId, onClose, onSuccess }: EditProductModalProps) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [productTypes, setProductTypes] = useState<ProductTypeOption[]>([]);
  const [productTypesLoading, setProductTypesLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    reference: '',
    designation: '',
    description: '',
    type: 'PART' as 'MACHINE' | 'PART' | 'CONSUMABLE',
    type_produit_id: '',
    categorie_id: '',
    numero_serie: '',
    marque: '',
    modele: '',
    annee_fabrication: '',
    prix_achat: '',
    prix_vente_public: '',
    prix_vente_grossiste: '',
    prix_vente_revendeur: '',
    tarif_location_journalier: '',
    seuil_alerte: '5',
  });

  const { toast } = useToast();
  const { hasAnyRole } = useAuth();
  // Magasinier may edit products but not their prices.
  const canEditPrices = !hasAnyRole(['MAGASINIER']);

  useEffect(() => {
    if (isOpen && productId) {
      // Load product detail, categories, and product types in parallel
      setLoadingProduct(true);
      setError(null);

      setCategoriesLoading(true);
      stockService.listCategories()
        .then(cats => setCategories(cats))
        .catch(() => setCategories([]))
        .finally(() => setCategoriesLoading(false));

      setProductTypesLoading(true);
      apiClient.get('/product-types', { params: { active_only: true } })
        .then(res => {
          const data = res.data?.data || [];
          setProductTypes(data);
        })
        .catch(() => setProductTypes([]))
        .finally(() => setProductTypesLoading(false));

      stockService.getProduct(productId)
        .then((p: ApiProductDetail) => {
          setFormData({
            reference: p.reference || '',
            designation: p.designation || '',
            description: p.description || '',
            type: p.type || 'PART',
            type_produit_id: p.type_produit_id || '',
            categorie_id: p.categorie_id || '',
            numero_serie: p.numero_serie || '',
            marque: p.marque || '',
            modele: p.modele || '',
            annee_fabrication: p.annee_fabrication ? String(p.annee_fabrication) : '',
            prix_achat: p.prix_achat ? String(p.prix_achat) : '',
            prix_vente_public: p.prix_vente_public ? String(p.prix_vente_public) : '',
            prix_vente_grossiste: p.prix_vente_grossiste ? String(p.prix_vente_grossiste) : '',
            prix_vente_revendeur: p.prix_vente_revendeur ? String(p.prix_vente_revendeur) : '',
            tarif_location_journalier: p.tarif_location_journalier ? String(p.tarif_location_journalier) : '',
            seuil_alerte: String(p.seuil_alerte ?? 5),
          });
        })
        .catch((err: any) => {
          setError(err?.message || 'Impossible de charger le produit');
        })
        .finally(() => setLoadingProduct(false));
    }
  }, [isOpen, productId]);

  const selectedType = productTypes.find(pt => pt.id === formData.type_produit_id);
  const baseType = selectedType?.base_type || formData.type;

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productId) return;
    setSaving(true);
    setError(null);

    try {
      const data: ProductUpdate = {
        reference: formData.reference || undefined,
        designation: formData.designation || undefined,
        description: formData.description || undefined,
        type_produit_id: formData.type_produit_id || undefined,
        categorie_id: formData.categorie_id || undefined,
        numero_serie: formData.numero_serie || undefined,
        marque: formData.marque || undefined,
        modele: formData.modele || undefined,
        annee_fabrication: formData.annee_fabrication ? parseInt(formData.annee_fabrication) : undefined,
        seuil_alerte: parseInt(formData.seuil_alerte) || 5,
        // Prices are omitted for roles that cannot edit them (e.g. magasinier);
        // the backend also rejects price changes from those roles (403).
        ...(canEditPrices ? {
          prix_achat: formData.prix_achat ? parseFloat(formData.prix_achat) : undefined,
          prix_vente_public: formData.prix_vente_public ? parseFloat(formData.prix_vente_public) : undefined,
          prix_vente_grossiste: formData.prix_vente_grossiste ? parseFloat(formData.prix_vente_grossiste) : undefined,
          prix_vente_revendeur: formData.prix_vente_revendeur ? parseFloat(formData.prix_vente_revendeur) : undefined,
          tarif_location_journalier: formData.tarif_location_journalier ? parseFloat(formData.tarif_location_journalier) : undefined,
        } : {}),
      };
      await stockService.updateProduct(productId, data);
      toast('Produit modifié avec succès', 'success');
      onSuccess();
      handleClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur lors de la modification';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Modifier le Produit">
      {loadingProduct ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
          <p className="text-slate-500 mt-2">Chargement du produit...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Référence"
              required
              value={formData.reference}
              onChange={e => setFormData({ ...formData, reference: e.target.value })}
              disabled={saving}
            />
            <Input
              label="Désignation"
              required
              value={formData.designation}
              onChange={e => setFormData({ ...formData, designation: e.target.value })}
              disabled={saving}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
              {productTypes.length > 0 ? (
                <select
                  className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                  value={formData.type_produit_id}
                  onChange={e => {
                    const pt = productTypes.find(t => t.id === e.target.value);
                    setFormData({
                      ...formData,
                      type_produit_id: e.target.value,
                      type: pt?.base_type || 'PART',
                    });
                  }}
                  disabled={saving || productTypesLoading}
                >
                  <option value="">Sélectionner...</option>
                  {productTypes.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.libelle}</option>
                  ))}
                </select>
              ) : (
                <select
                  className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value as any })}
                  disabled={saving}
                >
                  <option value="PART">Pièce</option>
                  <option value="MACHINE">Machine</option>
                  <option value="CONSUMABLE">Consommable</option>
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
              <select
                className="w-full border-slate-300 rounded-md shadow-sm text-sm"
                value={formData.categorie_id}
                onChange={e => setFormData({ ...formData, categorie_id: e.target.value })}
                disabled={saving || categoriesLoading}
              >
                <option value="">Sélectionner...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.libelle}</option>
                ))}
              </select>
            </div>
          </div>

          {baseType === 'MACHINE' && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-4">
              <p className="text-xs font-bold text-blue-700 uppercase">Informations Machine</p>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="N° Série"
                  value={formData.numero_serie}
                  onChange={e => setFormData({ ...formData, numero_serie: e.target.value })}
                  disabled={saving}
                />
                <Input
                  label="Marque"
                  value={formData.marque}
                  onChange={e => setFormData({ ...formData, marque: e.target.value })}
                  disabled={saving}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Modèle"
                  value={formData.modele}
                  onChange={e => setFormData({ ...formData, modele: e.target.value })}
                  disabled={saving}
                />
                <Input
                  label="Année de Fabrication"
                  type="number"
                  value={formData.annee_fabrication}
                  onChange={e => setFormData({ ...formData, annee_fabrication: e.target.value })}
                  disabled={saving}
                />
              </div>
            </div>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-4">
            <p className="text-xs font-bold text-slate-600 uppercase">Tarification (FCFA)</p>
            {!canEditPrices && (
              <p className="text-xs text-slate-500 italic">
                Vous n'êtes pas autorisé à modifier les prix.
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Prix d'Achat"
                type="number"
                min={0}
                value={formData.prix_achat}
                onChange={e => setFormData({ ...formData, prix_achat: e.target.value })}
                disabled={saving || !canEditPrices}
              />
              <Input
                label="Prix Vente Public"
                type="number"
                min={0}
                value={formData.prix_vente_public}
                onChange={e => setFormData({ ...formData, prix_vente_public: e.target.value })}
                disabled={saving || !canEditPrices}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Prix Grossiste"
                type="number"
                min={0}
                value={formData.prix_vente_grossiste}
                onChange={e => setFormData({ ...formData, prix_vente_grossiste: e.target.value })}
                disabled={saving || !canEditPrices}
              />
              <Input
                label="Prix Revendeur"
                type="number"
                min={0}
                value={formData.prix_vente_revendeur}
                onChange={e => setFormData({ ...formData, prix_vente_revendeur: e.target.value })}
                disabled={saving || !canEditPrices}
              />
              <Input
                label="Tarif Location/Jour"
                type="number"
                min={0}
                value={formData.tarif_location_journalier}
                onChange={e => setFormData({ ...formData, tarif_location_journalier: e.target.value })}
                disabled={saving || !canEditPrices}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Seuil d'Alerte Stock"
              type="number"
              min={0}
              value={formData.seuil_alerte}
              onChange={e => setFormData({ ...formData, seuil_alerte: e.target.value })}
              disabled={saving}
            />
            <div className="col-span-1" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              className="w-full border-slate-300 rounded-md p-2 text-sm"
              rows={3}
              placeholder="Description du produit..."
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              disabled={saving}
            />
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-4">
            <Button variant="ghost" type="button" onClick={handleClose} disabled={saving}>Annuler</Button>
            <Button type="submit" disabled={saving || !formData.reference || !formData.designation}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer les Modifications'
              )}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

// --- Detail Drawer ---

interface ProductDetailProps {
  product: Product;
  onClose: () => void;
  onAddMovement: () => void;
  onEdit: () => void;
  canWrite?: boolean;
  onDeleted?: () => void;
}

const ProductDetail = ({ product, onClose, onAddMovement, onEdit, canWrite = true, onDeleted }: ProductDetailProps) => {
  const [activeTab, setActiveTab] = useState<'info' | 'pricing' | 'stock' | 'history'>('info');
  const [movements, setMovements] = useState<ApiStockMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [stockBySite, setStockBySite] = useState<Record<string, number>>({});
  const [stockBySiteLoading, setStockBySiteLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (activeTab === 'stock') {
      setStockBySiteLoading(true);
      stockService.getProductStock(product.id)
        .then(result => setStockBySite(result.stock_by_site || {}))
        .catch(() => setStockBySite({}))
        .finally(() => setStockBySiteLoading(false));
    }
  }, [activeTab, product.id]);

  useEffect(() => {
    if (activeTab === 'history') {
      setMovementsLoading(true);
      stockService.listMovements({ product_id: product.id, page_size: showAllHistory ? 100 : 10 })
        .then(result => setMovements(result.data))
        .catch(() => setMovements([]))
        .finally(() => setMovementsLoading(false));
    }
  }, [activeTab, product.id, showAllHistory]);

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl z-40 transform transition-transform duration-300 overflow-y-auto border-l border-slate-200">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-slate-200 flex justify-between items-start">
        <div>
           <div className="flex items-center gap-2 mb-1">
             <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{product.category}</span>
             <StockStatusBadge product={product} />
           </div>
           <h2 className="text-2xl font-bold text-slate-900">{product.name}</h2>
           <p className="text-slate-500 text-sm font-mono">{product.reference}</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-5 h-5 text-slate-500" /></button>
      </div>

      {/* Actions */}
      {canWrite && (
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex gap-3">
          <Button size="sm" onClick={onAddMovement} icon={ArrowRightLeft}>Nouveau Mouvement</Button>
          <Button size="sm" variant="secondary" icon={Edit} onClick={onEdit}>Modifier</Button>
          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={async () => {
            if (!confirm(`Êtes-vous sûr de vouloir supprimer "${product.name}" ? Cette action est irréversible.`)) return;
            try {
              await apiClient.delete(`/products/${product.id}`);
              toast('Produit supprimé', 'success');
              onDeleted?.();
              onClose();
            } catch (err: any) {
              toast(err?.message || 'Impossible de supprimer ce produit', 'error');
            }
          }}>Supprimer</Button>
        </div>
      )}

      {/* Tabs */}
      <div className="px-6 pt-4 border-b border-slate-200">
        <nav className="-mb-px flex space-x-6">
          {['info', 'pricing', 'stock', 'history'].map((tab) => (
             <button
               key={tab}
               onClick={() => setActiveTab(tab as any)}
               className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                 activeTab === tab 
                   ? 'border-primary-500 text-primary-600'
                   : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
               }`}
             >
               {tab === 'info' && 'Informations'}
               {tab === 'pricing' && 'Tarification'}
               {tab === 'stock' && 'Stocks par Site'}
               {tab === 'history' && 'Historique'}
             </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'info' && (
          <div className="space-y-6 animate-in fade-in">
             <div className="aspect-video w-full bg-slate-100 rounded-lg overflow-hidden border border-slate-200 mb-6">
               {product.imageUrl ? (
                 <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
               ) : (
                 <div className="w-full h-full flex items-center justify-center text-slate-400">Aucune image</div>
               )}
             </div>
             
             <div className="grid grid-cols-2 gap-6">
               <div>
                 <label className="text-xs text-slate-500 uppercase">Type</label>
                 <p className="font-medium text-slate-900">{product.type === ProductType.MACHINE ? 'Machine (Sérialisée)' : 'Pièce / Consommable'}</p>
               </div>
               <div>
                 <label className="text-xs text-slate-500 uppercase">Seuil d'alerte</label>
                 <p className="font-medium text-slate-900">{product.minStockThreshold} unités</p>
               </div>
               <div className="col-span-2">
                 <label className="text-xs text-slate-500 uppercase">Description</label>
                 <p className="text-sm text-slate-700 mt-1">{product.description || "Aucune description disponible."}</p>
               </div>
             </div>
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="space-y-6 animate-in fade-in">
             <Card className="bg-slate-50">
               <div className="flex justify-between items-center mb-2">
                 <span className="text-sm font-medium text-slate-600">Prix de Vente (HT)</span>
                 <span className="text-lg font-bold text-slate-900">{formatCurrency(product.priceSale)}</span>
               </div>
               <div className="h-px bg-slate-200 my-2" />
               <div className="flex justify-between items-center">
                 <span className="text-sm font-medium text-slate-600">Tarif Location (Jour)</span>
                 <span className="text-lg font-bold text-slate-900">{product.priceRental > 0 ? formatCurrency(product.priceRental) : 'N/A'}</span>
               </div>
             </Card>
             <p className="text-xs text-slate-500 italic">
               * Les prix indiqués sont des prix de base. Des remises peuvent être appliquées lors de la création du devis.
             </p>
          </div>
        )}

        {activeTab === 'stock' && (
          <div className="space-y-4 animate-in fade-in">
             <h3 className="font-medium text-slate-900">Répartition Géographique</h3>
             {stockBySiteLoading ? (
               <div className="text-center py-8">
                 <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                 <p className="text-slate-500 mt-2">Chargement...</p>
               </div>
             ) : (
             <div className="space-y-3">
               {Object.entries(stockBySite).length > 0 ? (
                 Object.entries(stockBySite).map(([site, qty]) => (
                   <div key={site} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-full text-blue-600"><MapPin className="w-4 h-4" /></div>
                        <span className="font-medium text-slate-700">{site}</span>
                      </div>
                      <span className="font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-full">{qty}</span>
                   </div>
                 ))
               ) : (
                 <p className="text-sm text-slate-500 text-center py-4">Aucun stock enregistré sur les sites.</p>
               )}
               <div className="flex items-center justify-between p-3 bg-slate-100 border border-slate-200 rounded-lg mt-4">
                  <span className="font-bold text-slate-700">TOTAL GLOBAL</span>
                  <span className="font-bold text-slate-900 text-lg">{product.stockTotal}</span>
               </div>
             </div>
             )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4 animate-in fade-in">
            <h3 className="font-medium text-slate-900">Mouvements Récents</h3>
            {movementsLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                <p className="text-slate-500 mt-2">Chargement...</p>
              </div>
            ) : movements.length > 0 ? (
              <>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                   <table className="min-w-full divide-y divide-slate-200">
                     <thead className="bg-slate-50">
                       <tr>
                         <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                         <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                         <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Statut</th>
                         <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Qté</th>
                       </tr>
                     </thead>
                     <tbody className="bg-white divide-y divide-slate-200">
                       {movements.map(m => (
                         <tr key={m.id}>
                           <td className="px-4 py-2 text-sm text-slate-600">
                             {new Date(m.date_mouvement).toLocaleDateString('fr-FR')}
                             <span className="text-slate-400 ml-1">{new Date(m.date_mouvement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                           </td>
                           <td className="px-4 py-2">
                             <MovementTypeBadge type={mapMovementType(m.type_mouvement)} />
                           </td>
                           <td className="px-4 py-2 text-center">
                             <MovementStatusBadge statut={m.statut} />
                           </td>
                           <td className="px-4 py-2 text-sm text-slate-900 text-right font-medium">{m.quantite}</td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                </div>
                {!showAllHistory && (
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => setShowAllHistory(true)}>Voir tout l'historique</Button>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <History className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p>Aucun mouvement enregistré</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Transform API product data to component format
// --- Signature Canvas Component ---

const SignatureCanvas = ({ onSignatureChange, label }: { onSignatureChange: (base64: string) => void; label: string }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      onSignatureChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onSignatureChange('');
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <button type="button" onClick={clear} className="text-xs text-red-500 hover:text-red-700">Effacer</button>
      </div>
      <canvas
        ref={canvasRef}
        width={300}
        height={100}
        className="border border-slate-300 rounded-md bg-white cursor-crosshair touch-none w-full"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
    </div>
  );
};

// --- Execution Modal (Sortie) ---

interface ExecutionSortieModalProps {
  isOpen: boolean;
  movement: ApiStockMovement | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ExecutionSortieModal = ({ isOpen, movement, onClose, onSuccess }: ExecutionSortieModalProps) => {
  const [signatureMagasinier, setSignatureMagasinier] = useState('');
  const [signatureReceptionnaire, setSignatureReceptionnaire] = useState('');
  const [nomReceptionnaire, setNomReceptionnaire] = useState('');
  const [observations, setObservations] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const canSubmit = nomReceptionnaire.length >= 2;

  const handleSubmit = async () => {
    if (!movement || !canSubmit) return;
    setLoading(true);
    try {
      await stockService.executeSortie(movement.id, {
        signature_magasinier: signatureMagasinier || undefined,
        signature_receptionnaire: signatureReceptionnaire || undefined,
        nom_receptionnaire: nomReceptionnaire,
        observations: observations || undefined,
      });
      const isTransfer = movement.type_mouvement === 'TRANSFERT';
      toast(isTransfer ? 'Sortie exécutée, matériel en transit' : 'Mouvement exécuté avec succès', 'success');
      onSuccess();
      onClose();
    } catch (err: any) {
      toast(err?.message || 'Erreur lors de l\'exécution', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSignatureMagasinier('');
      setSignatureReceptionnaire('');
      setNomReceptionnaire('');
      setObservations('');
    }
  }, [isOpen]);

  if (!movement) return null;

  const isTransfer = movement.type_mouvement === 'TRANSFERT';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isTransfer ? 'Exécuter la sortie (transfert)' : 'Exécuter le mouvement'}>
      <div className="space-y-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Produit :</span><span className="font-medium">{movement.product_nom}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Quantité :</span><span className="font-bold">{movement.quantite}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Site source :</span><span>{movement.site_source_nom}</span></div>
          {isTransfer && <div className="flex justify-between"><span className="text-slate-500">Site destination :</span><span>{movement.site_destination_nom}</span></div>}
        </div>

        <Input
          label="Nom du réceptionnaire"
          placeholder="Nom complet de la personne qui prend le matériel"
          value={nomReceptionnaire}
          onChange={e => setNomReceptionnaire(e.target.value)}
          required
        />

        <SignatureCanvas label="Signature du magasinier (optionnel)" onSignatureChange={setSignatureMagasinier} />
        <SignatureCanvas label="Signature du réceptionnaire (optionnel)" onSignatureChange={setSignatureReceptionnaire} />
        <p className="text-xs text-slate-500">Les signatures électroniques sont facultatives — les signatures physiques sur le bon papier suffisent.</p>

        <div>
          <label className="text-sm font-medium text-slate-700">Observations (optionnel)</label>
          <textarea
            className="w-full border-slate-300 rounded-md p-2 text-sm mt-1"
            rows={2}
            placeholder="Notes sur l'état du matériel, remarques..."
            value={observations}
            onChange={e => setObservations(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenTool className="w-4 h-4 mr-2" />}
            {isTransfer ? 'Confirmer la sortie' : 'Exécuter'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// --- Execution Modal (Entree / Réception) ---

interface ExecutionEntreeModalProps {
  isOpen: boolean;
  movement: ApiStockMovement | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ExecutionEntreeModal = ({ isOpen, movement, onClose, onSuccess }: ExecutionEntreeModalProps) => {
  const [signatureMagasinier, setSignatureMagasinier] = useState('');
  const [signatureRemettant, setSignatureRemettant] = useState('');
  const [nomRemettant, setNomRemettant] = useState('');
  const [observations, setObservations] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const canSubmit = nomRemettant.length >= 2;

  const handleSubmit = async () => {
    if (!movement || !canSubmit) return;
    setLoading(true);
    try {
      await stockService.executeEntree(movement.id, {
        signature_magasinier: signatureMagasinier || undefined,
        signature_remettant: signatureRemettant || undefined,
        nom_remettant: nomRemettant,
        observations: observations || undefined,
      });
      toast('Réception confirmée, stock mis à jour', 'success');
      onSuccess();
      onClose();
    } catch (err: any) {
      toast(err?.message || 'Erreur lors de la réception', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSignatureMagasinier('');
      setSignatureRemettant('');
      setNomRemettant('');
      setObservations('');
    }
  }, [isOpen]);

  if (!movement) return null;

  const isTransfer = movement.type_mouvement === 'TRANSFERT';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isTransfer ? 'Réceptionner le transfert' : 'Exécuter l\'entrée'}>
      <div className="space-y-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Produit :</span><span className="font-medium">{movement.product_nom}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Quantité :</span><span className="font-bold">{movement.quantite}</span></div>
          {isTransfer && <div className="flex justify-between"><span className="text-slate-500">Provenance :</span><span>{movement.site_source_nom}</span></div>}
          <div className="flex justify-between"><span className="text-slate-500">Site réception :</span><span>{isTransfer ? movement.site_destination_nom : movement.site_source_nom}</span></div>
        </div>

        <Input
          label="Nom du remettant"
          placeholder="Nom complet de la personne qui remet le matériel"
          value={nomRemettant}
          onChange={e => setNomRemettant(e.target.value)}
          required
        />

        <SignatureCanvas label="Signature du magasinier récepteur (optionnel)" onSignatureChange={setSignatureMagasinier} />
        <SignatureCanvas label="Signature du remettant (optionnel)" onSignatureChange={setSignatureRemettant} />
        <p className="text-xs text-slate-500">Les signatures électroniques sont facultatives — les signatures physiques sur le bon papier suffisent.</p>

        <div>
          <label className="text-sm font-medium text-slate-700">Observations (optionnel)</label>
          <textarea
            className="w-full border-slate-300 rounded-md p-2 text-sm mt-1"
            rows={2}
            placeholder="État du matériel à la réception, remarques..."
            value={observations}
            onChange={e => setObservations(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenTool className="w-4 h-4 mr-2" />}
            Confirmer la réception
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const transformStockToProduct = (product: ProductListItem): Product => ({
  id: product.id,
  reference: product.reference,
  name: product.designation,
  category: product.categorie_nom || 'Non catégorisé',
  type: mapProductType(product.type),
  isSerialized: product.is_serialized,
  minStockThreshold: product.seuil_alerte,
  stockTotal: product.stock_total ?? 0,
  stockDisponible: product.stock_disponible ?? 0,
  stockReservee: product.stock_reservee ?? 0,
  stockEnLocation: product.stock_en_location ?? 0,
  stockEnLoa: product.stock_en_loa ?? 0,
  stockBySite: {},
  priceSale: product.prix_vente_public,
  priceRental: product.tarif_location_journalier,
  purchasePrice: 0,  // not returned by /products list endpoint
  imageUrl: product.image_url,
  status: 'OK',
  description: undefined,
});

// --- Main Stock View ---

export const StockModule = ({ canWrite, selectedSiteId }: { canWrite?: boolean; selectedSiteId?: string }) => {
  const canWriteStock = canWrite ?? false;
  const { user, hasAnyRole } = useAuth();
  const canValidate = hasAnyRole(['GERANT', 'ADMIN']);
  const canSeePending = hasAnyRole(['GERANT', 'ADMIN', 'MAGASINIER']);
  const canExecute = hasAnyRole(['MAGASINIER', 'ADMIN']);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
  const [isCreateProductOpen, setIsCreateProductOpen] = useState(false);
  const [isEditProductOpen, setIsEditProductOpen] = useState(false);
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [activeView, setActiveView] = useState<'stock' | 'pending' | 'to-execute' | 'in-transit'>('stock');
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'xlsx' | null>(null);

  const handleExportStock = async (format: 'pdf' | 'xlsx') => {
    setExportingFormat(format);
    try {
      await stockService.downloadStock(format, {
        site_id: selectedSiteId && selectedSiteId !== 'all' ? selectedSiteId : undefined,
      });
    } catch (err: any) {
      toast(err?.message || "Échec de l'export du stock", 'error');
    } finally {
      setExportingFormat(null);
    }
  };

  // Execution modal state
  const [executionSortieOpen, setExecutionSortieOpen] = useState(false);
  const [executionEntreeOpen, setExecutionEntreeOpen] = useState(false);
  const [executionTarget, setExecutionTarget] = useState<ApiStockMovement | null>(null);

  // Workflow movements state
  const [toExecuteMovements, setToExecuteMovements] = useState<ApiStockMovement[]>([]);
  const [toExecuteCount, setToExecuteCount] = useState(0);
  const [toExecuteLoading, setToExecuteLoading] = useState(false);
  const [inTransitMovements, setInTransitMovements] = useState<ApiStockMovement[]>([]);
  const [inTransitCount, setInTransitCount] = useState(0);
  const [inTransitLoading, setInTransitLoading] = useState(false);

  // Server-side pagination & sort
  const PAGE_SIZE = 20;
  const [stockPage, setStockPage] = useState(1);
  const [stockPagination, setStockPagination] = useState({ totalPages: 1, totalItems: 0 });

  type StockSortKey = 'reference' | 'designation' | 'categorie' | 'stock' | 'status';
  const [stockSort, setStockSort] = useState<{ key: StockSortKey; dir: 'asc' | 'desc' }>({ key: 'designation', dir: 'asc' });

  const sortKeyMap: Record<StockSortKey, string> = {
    reference: 'reference', designation: 'designation',
    categorie: 'categorie_nom', stock: 'designation', // stock can't be sorted server-side easily, fallback
    status: 'status',
  };

  const toggleStockSort = (key: StockSortKey) => {
    setStockSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    setStockPage(1);
  };

  const StockSortIcon = ({ col }: { col: StockSortKey }) => {
    if (stockSort.key !== col) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return stockSort.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />;
  };

  // API state
  const [products, setProducts] = useState<Product[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pending movements state
  const [pendingMovements, setPendingMovements] = useState<ApiStockMovement[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectMotif, setRejectMotif] = useState('');
  const [validatingId, setValidatingId] = useState<string | null>(null);

  const { toast } = useToast();

  const fetchPendingMovements = useCallback(async () => {
    setPendingLoading(true);
    try {
      const result = await stockService.getPendingMovements(1, 50);
      setPendingMovements(result.data);
      setPendingCount(result.pagination.total_items);
    } catch {
      setPendingMovements([]);
      setPendingCount(0);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const fetchToExecuteMovements = useCallback(async () => {
    setToExecuteLoading(true);
    try {
      const result = await stockService.listMovements({ statut: 'VALIDE', page: 1, page_size: 50 });
      setToExecuteMovements(result.data);
      setToExecuteCount(result.pagination.total_items);
    } catch {
      setToExecuteMovements([]);
      setToExecuteCount(0);
    } finally {
      setToExecuteLoading(false);
    }
  }, []);

  const fetchInTransitMovements = useCallback(async () => {
    setInTransitLoading(true);
    try {
      const result = await stockService.listMovements({ statut: 'EN_TRANSIT', page: 1, page_size: 50 });
      setInTransitMovements(result.data);
      setInTransitCount(result.pagination.total_items);
    } catch {
      setInTransitMovements([]);
      setInTransitCount(0);
    } finally {
      setInTransitLoading(false);
    }
  }, []);

  // Debounce search (400ms)
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedFilter(filter); setStockPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [filter]);

  // Fetch products from API (server-side pagination)
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [stockResult, sitesResult] = await Promise.all([
        stockService.listStock({
          page: stockPage,
          page_size: PAGE_SIZE,
          search: debouncedFilter || undefined,
          site_id: selectedSiteId && selectedSiteId !== 'all' ? selectedSiteId : undefined,
          sort_by: sortKeyMap[stockSort.key],
          sort_dir: stockSort.dir,
        }),
        stockService.listSites(),
      ]);

      setProducts(stockResult.data.map(transformStockToProduct));
      setStockPagination({
        totalPages: stockResult.pagination.total_pages,
        totalItems: stockResult.pagination.total_items,
      });
      setSites(sitesResult);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement du stock');
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedFilter, selectedSiteId, stockPage, stockSort]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (canSeePending) {
      fetchPendingMovements();
    }
    if (canExecute) {
      fetchToExecuteMovements();
      fetchInTransitMovements();
    }
  }, [canSeePending, canExecute, fetchPendingMovements, fetchToExecuteMovements, fetchInTransitMovements]);

  const handleProductClick = (p: Product) => {
    setSelectedProduct(p);
    setIsDetailOpen(true);
  };

  const handleCreateMovement = (p?: Product) => {
    if (p) setSelectedProduct(p);
    setIsMovementModalOpen(true);
  };

  const handleMovementSuccess = () => {
    fetchProducts();
    if (canSeePending) fetchPendingMovements();
    if (canExecute) { fetchToExecuteMovements(); fetchInTransitMovements(); }
  };

  const handleValidateMovement = async (movementId: string) => {
    setValidatingId(movementId);
    try {
      await stockService.validateMovement(movementId);
      toast('Mouvement validé, en attente d\'exécution physique', 'success');
      fetchPendingMovements();
      if (canExecute) fetchToExecuteMovements();
    } catch (err: any) {
      toast(err?.message || 'Erreur lors de la validation', 'error');
    } finally {
      setValidatingId(null);
    }
  };

  const handleExecutionSuccess = () => {
    fetchProducts();
    if (canExecute) { fetchToExecuteMovements(); fetchInTransitMovements(); }
    if (canSeePending) fetchPendingMovements();
  };

  const handleRejectMovement = async () => {
    if (!rejectTarget || rejectMotif.length < 5) return;
    setValidatingId(rejectTarget);
    try {
      await stockService.rejectMovement(rejectTarget, rejectMotif);
      toast('Mouvement rejeté', 'success');
      setRejectModalOpen(false);
      setRejectTarget(null);
      setRejectMotif('');
      fetchPendingMovements();
    } catch (err: any) {
      toast(err?.message || 'Erreur lors du rejet', 'error');
    } finally {
      setValidatingId(null);
    }
  };

  const filteredProducts = products;

  return (
    <div className="relative min-h-[500px]">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800">Stock & Catalogue</h2>
           <p className="text-sm text-slate-500">Gérez vos machines, pièces et consommables.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Button
            icon={FileText}
            variant="secondary"
            onClick={() => handleExportStock('pdf')}
            disabled={exportingFormat !== null}
          >
            {exportingFormat === 'pdf' ? 'Export…' : 'PDF'}
          </Button>
          <Button
            icon={FileSpreadsheet}
            variant="secondary"
            onClick={() => handleExportStock('xlsx')}
            disabled={exportingFormat !== null}
          >
            {exportingFormat === 'xlsx' ? 'Export…' : 'Excel'}
          </Button>
          {canWriteStock && (
            <>
              <Button icon={ArrowRightLeft} variant="secondary" onClick={() => setIsMovementModalOpen(true)}>Mouvement Rapide</Button>
              <Button icon={Plus} onClick={() => setIsCreateProductOpen(true)}>Nouveau Produit</Button>
            </>
          )}
        </div>
      </div>

      {/* View Tabs */}
      {(canValidate || canExecute) && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            onClick={() => setActiveView('stock')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeView === 'stock'
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Catalogue
          </button>
          {canSeePending && (
            <button
              onClick={() => { setActiveView('pending'); fetchPendingMovements(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeView === 'pending'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              À valider
              {pendingCount > 0 && (
                <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                  activeView === 'pending' ? 'bg-yellow-400 text-slate-900' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          )}
          {canExecute && (
            <button
              onClick={() => { setActiveView('to-execute'); fetchToExecuteMovements(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeView === 'to-execute'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              À exécuter
              {toExecuteCount > 0 && (
                <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                  activeView === 'to-execute' ? 'bg-blue-400 text-white' : 'bg-blue-100 text-blue-800'
                }`}>
                  {toExecuteCount}
                </span>
              )}
            </button>
          )}
          {canExecute && (
            <button
              onClick={() => { setActiveView('in-transit'); fetchInTransitMovements(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeView === 'in-transit'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              En transit
              {inTransitCount > 0 && (
                <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                  activeView === 'in-transit' ? 'bg-orange-400 text-white' : 'bg-orange-100 text-orange-800'
                }`}>
                  {inTransitCount}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Filters (only in stock view) */}
      {activeView === 'stock' && (
        <div className="bg-white p-4 rounded-lg border border-slate-200 mb-6 flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              className="pl-9 w-full border-slate-300 rounded-md text-sm py-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Rechercher référence, nom..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* To Execute View */}
      {activeView === 'to-execute' && canExecute && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Qté</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Site</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {toExecuteLoading && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500"><Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-3" /><p>Chargement...</p></td></tr>
              )}
              {!toExecuteLoading && toExecuteMovements.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500"><CheckCircle className="w-8 h-8 mx-auto text-green-300 mb-3" /><p>Aucun mouvement en attente d'exécution</p></td></tr>
              )}
              {!toExecuteLoading && toExecuteMovements.map(m => {
                const isEntry = m.type_mouvement === 'ENTREE' || m.type_mouvement === 'RETOUR';
                return (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(m.date_mouvement).toLocaleDateString('fr-FR')}
                      <span className="text-slate-400 ml-1 text-xs">{new Date(m.date_mouvement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">{m.product_nom || '—'}</div>
                      <div className="text-xs text-slate-500 font-mono">{m.product_reference || ''}</div>
                    </td>
                    <td className="px-6 py-4"><MovementTypeBadge type={mapMovementType(m.type_mouvement)} /></td>
                    <td className="px-6 py-4 text-center text-sm font-bold text-slate-900">{m.quantite}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {m.site_source_nom || '—'}
                      {m.site_destination_nom && <span className="text-slate-400"> → {m.site_destination_nom}</span>}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        size="sm"
                        onClick={() => {
                          setExecutionTarget(m);
                          if (isEntry) {
                            setExecutionEntreeOpen(true);
                          } else {
                            setExecutionSortieOpen(true);
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <PenTool className="w-3 h-3" />
                        <span className="ml-1">{isEntry ? 'Exécuter entrée' : 'Exécuter sortie'}</span>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* In Transit View */}
      {activeView === 'in-transit' && canExecute && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date sortie</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produit</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Qté</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Trajet</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sorti par</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {inTransitLoading && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500"><Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-3" /><p>Chargement...</p></td></tr>
              )}
              {!inTransitLoading && inTransitMovements.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500"><Truck className="w-8 h-8 mx-auto text-slate-300 mb-3" /><p>Aucun transfert en transit</p></td></tr>
              )}
              {!inTransitLoading && inTransitMovements.map(m => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {m.executed_sortie_at ? new Date(m.executed_sortie_at).toLocaleDateString('fr-FR') : new Date(m.date_mouvement).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900">{m.product_nom || '—'}</div>
                    <div className="text-xs text-slate-500 font-mono">{m.product_reference || ''}</div>
                  </td>
                  <td className="px-6 py-4 text-center text-sm font-bold text-slate-900">{m.quantite}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className="text-slate-600">{m.site_source_nom}</span>
                    <span className="text-orange-500 mx-1">→</span>
                    <span className="font-medium text-slate-900">{m.site_destination_nom}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{m.nom_sortie_receptionnaire || '—'}</td>
                  <td className="px-6 py-4 text-right">
                    <Button
                      size="sm"
                      onClick={() => { setExecutionTarget(m); setExecutionEntreeOpen(true); }}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <Package className="w-3 h-3" />
                      <span className="ml-1">Réceptionner</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending Movements View */}
      {activeView === 'pending' && canSeePending && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Qté</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Site</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Réf. Doc</th>
                {canValidate && <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {pendingLoading && (
                <tr>
                  <td colSpan={canValidate ? 7 : 6} className="px-6 py-12 text-center text-slate-500">
                    <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-3" />
                    <p>Chargement...</p>
                  </td>
                </tr>
              )}
              {!pendingLoading && pendingMovements.length === 0 && (
                <tr>
                  <td colSpan={canValidate ? 7 : 6} className="px-6 py-12 text-center text-slate-500">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-300 mb-3" />
                    <p>Aucun mouvement en attente de validation</p>
                  </td>
                </tr>
              )}
              {!pendingLoading && pendingMovements.map(m => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {new Date(m.date_mouvement).toLocaleDateString('fr-FR')}
                    <span className="text-slate-400 ml-1 text-xs">
                      {new Date(m.date_mouvement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-900">{m.product_nom || '—'}</div>
                    <div className="text-xs text-slate-500 font-mono">{m.product_reference || ''}</div>
                  </td>
                  <td className="px-6 py-4">
                    <MovementTypeBadge type={mapMovementType(m.type_mouvement)} />
                  </td>
                  <td className="px-6 py-4 text-center text-sm font-bold text-slate-900">{m.quantite}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{m.site_source_nom || '—'}</td>
                  <td className="px-6 py-4 text-sm text-slate-500 font-mono">{m.reference_document || '—'}</td>
                  {canValidate && (
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleValidateMovement(m.id)}
                        disabled={validatingId === m.id}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {validatingId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        <span className="ml-1">Valider</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setRejectTarget(m.id);
                          setRejectMotif('');
                          setRejectModalOpen(true);
                        }}
                        disabled={validatingId === m.id}
                      >
                        <XCircle className="w-3 h-3" />
                        <span className="ml-1">Rejeter</span>
                      </Button>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Data Table */}
      {activeView === 'stock' && (
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleStockSort('reference')}>Référence / Produit<StockSortIcon col="reference" /></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleStockSort('categorie')}>Catégorie<StockSortIcon col="categorie" /></th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleStockSort('stock')}>Total<StockSortIcon col="stock" /></th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Dispo</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Réservé</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">LOA</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleStockSort('status')}>Statut<StockSortIcon col="status" /></th>
              <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-slate-500">
                  <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-3" />
                  <p>Chargement du stock...</p>
                </td>
              </tr>
            )}
            {!isLoading && error && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-red-500">
                  <AlertTriangle className="w-8 h-8 mx-auto text-red-400 mb-3" />
                  <p>{error}</p>
                  <Button variant="ghost" onClick={fetchProducts} className="mt-2">
                    Réessayer
                  </Button>
                </td>
              </tr>
            )}
            {!isLoading && !error && filteredProducts.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-slate-500">
                  <Package className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                  <p>Aucun produit trouvé</p>
                </td>
              </tr>
            )}
            {!isLoading && !error && filteredProducts.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0 bg-slate-100 rounded-md overflow-hidden border border-slate-200">
                      {p.imageUrl ? <img src={p.imageUrl} className="h-full w-full object-cover" /> : <Package className="h-full w-full p-2 text-slate-400" />}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-bold text-slate-900 hover:text-primary-600 cursor-pointer" onClick={() => handleProductClick(p)}>{p.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{p.reference}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {p.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="group relative inline-block">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold bg-slate-100 text-slate-800 cursor-help border border-slate-200">
                       {p.stockTotal}
                    </span>
                    {Object.keys(p.stockBySite).length > 0 && (
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 hidden group-hover:block bg-slate-800 text-white text-xs rounded shadow-lg p-2 z-20">
                        <div className="font-semibold border-b border-slate-600 pb-1 mb-1">Par site</div>
                        {Object.entries(p.stockBySite).map(([site, qty]) => (
                          <div key={site} className="flex justify-between"><span>{site}:</span> <span>{qty}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-green-700">
                  {p.stockDisponible}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                  {p.stockReservee > 0 ? <span className="font-bold text-amber-600">{p.stockReservee}</span> : <span className="text-slate-300">0</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                  {p.stockEnLocation > 0 ? <span className="font-bold text-blue-600">{p.stockEnLocation}</span> : <span className="text-slate-300">0</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                  {p.stockEnLoa > 0 ? <span className="font-bold text-purple-600">{p.stockEnLoa}</span> : <span className="text-slate-300">0</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <StockStatusBadge product={p} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => handleCreateMovement(p)} className="text-slate-400 hover:text-blue-600" title="Mouvement"><ArrowRightLeft className="w-4 h-4" /></button>
                    <button onClick={() => handleProductClick(p)} className="text-slate-400 hover:text-primary-600" title="Détails"><ChevronRight className="w-5 h-5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        {stockPagination.totalPages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-slate-200">
            <div className="text-sm text-slate-500">
              {stockPagination.totalItems} produit{stockPagination.totalItems > 1 ? 's' : ''}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={stockPage <= 1}
                onClick={() => setStockPage(p => p - 1)}
              >
                Précédent
              </Button>
              <span className="text-sm text-slate-600">
                Page {stockPage} sur {stockPagination.totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={stockPage >= stockPagination.totalPages}
                onClick={() => setStockPage(p => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Reject Modal */}
      <Modal isOpen={rejectModalOpen} onClose={() => { setRejectModalOpen(false); setRejectTarget(null); }} title="Rejeter le mouvement">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Veuillez indiquer le motif du rejet (minimum 5 caractères).</p>
          <textarea
            className="w-full border-slate-300 rounded-md p-3 text-sm"
            rows={3}
            placeholder="Motif du rejet..."
            value={rejectMotif}
            onChange={e => setRejectMotif(e.target.value)}
          />
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => { setRejectModalOpen(false); setRejectTarget(null); }}>Annuler</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRejectMovement}
              disabled={rejectMotif.length < 5 || validatingId === rejectTarget}
            >
              {validatingId === rejectTarget ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirmer le rejet
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detail Drawer */}
      {isDetailOpen && selectedProduct && (
        <>
          <div className="fixed inset-0 bg-slate-900/50 z-30 transition-opacity" onClick={() => setIsDetailOpen(false)} />
          <ProductDetail
            product={selectedProduct}
            onClose={() => setIsDetailOpen(false)}
            onAddMovement={() => handleCreateMovement(selectedProduct)}
            onEdit={() => {
              setEditProductId(selectedProduct.id);
              setIsEditProductOpen(true);
            }}
            canWrite={canWriteStock}
            onDeleted={fetchProducts}
          />
        </>
      )}

      {/* Execution Modals */}
      <ExecutionSortieModal
        isOpen={executionSortieOpen}
        movement={executionTarget}
        onClose={() => { setExecutionSortieOpen(false); setExecutionTarget(null); }}
        onSuccess={handleExecutionSuccess}
      />
      <ExecutionEntreeModal
        isOpen={executionEntreeOpen}
        movement={executionTarget}
        onClose={() => { setExecutionEntreeOpen(false); setExecutionTarget(null); }}
        onSuccess={handleExecutionSuccess}
      />

      {/* Movement Modal with Confirmation inside */}
      <CreateMovementModal
        isOpen={isMovementModalOpen}
        onClose={() => setIsMovementModalOpen(false)}
        product={selectedProduct || filteredProducts[0]}
        sites={sites}
        onSuccess={handleMovementSuccess}
      />

      {/* Create Product Modal */}
      <CreateProductModal
        isOpen={isCreateProductOpen}
        onClose={() => setIsCreateProductOpen(false)}
        onSuccess={fetchProducts}
      />

      {/* Edit Product Modal */}
      <EditProductModal
        isOpen={isEditProductOpen}
        productId={editProductId}
        onClose={() => {
          setIsEditProductOpen(false);
          setEditProductId(null);
        }}
        onSuccess={() => {
          fetchProducts();
          // Refresh detail drawer if still open
          if (selectedProduct && editProductId === selectedProduct.id) {
            setIsDetailOpen(false);
          }
        }}
      />
    </div>
  );
};
